/**
 * Sorting, filtering, ranking and trade-off analysis (spec sections 12, 13, 33).
 *
 * The invariant that matters most: a product whose sort metric is unavailable
 * always sorts *last*, in both directions. Treating a missing value as 0 would
 * make an incomplete product the cheapest-per-100g in the set (section 22).
 */

import type { Product } from '@/types/product';
import {
  METRIC_DIRECTION,
  METRICS,
  percentDifference,
  type MetricId,
  type ProductMetrics,
} from './calculations';
import { scoreAll, type ScoringProfile, type ValueScore, DEFAULT_PROFILE } from './scoring';

// ---------------------------------------------------------------------------
// Sort options (section 13)
// ---------------------------------------------------------------------------

export type SortGroup = 'value' | 'nutrition' | 'price' | 'package';

export interface SortOption {
  id: string;
  group: SortGroup;
  label: string;
  metric: MetricId | 'valueScore';
  /** Overrides the metric's natural direction when needed. */
  direction?: 'higher' | 'lower';
}

export const SORT_OPTIONS: SortOption[] = [
  // BEST VALUE
  { id: 'value_score', group: 'value', label: 'Value score', metric: 'valueScore' },
  { id: 'protein_per_dollar', group: 'value', label: 'Protein per dollar', metric: 'proteinPerDollar' },
  { id: 'kcal_per_dollar', group: 'value', label: 'Calories per dollar', metric: 'kcalPerDollar' },
  { id: 'price_per_100kcal', group: 'value', label: 'Lowest price per 100 kcal', metric: 'pricePer100kcal' },
  { id: 'price_per_10g_protein', group: 'value', label: 'Lowest price per 10 g protein', metric: 'pricePer10gProtein' },
  // NUTRITION
  { id: 'protein_high', group: 'nutrition', label: 'Highest protein', metric: 'protein100' },
  { id: 'protein_pct', group: 'nutrition', label: 'Highest protein %', metric: 'proteinPctCalories' },
  { id: 'kcal_low', group: 'nutrition', label: 'Lowest calories', metric: 'kcal100', direction: 'lower' },
  { id: 'kcal_high', group: 'nutrition', label: 'Highest calories', metric: 'kcal100', direction: 'higher' },
  { id: 'fibre_high', group: 'nutrition', label: 'Highest fibre', metric: 'fibre100' },
  { id: 'sugar_low', group: 'nutrition', label: 'Lowest sugar', metric: 'sugar100' },
  { id: 'sodium_low', group: 'nutrition', label: 'Lowest sodium', metric: 'sodium100' },
  // PRICE
  { id: 'price_low', group: 'price', label: 'Lowest price', metric: 'price' },
  { id: 'price_per_100g', group: 'price', label: 'Lowest price per 100 g', metric: 'pricePer100g' },
  { id: 'price_per_100ml', group: 'price', label: 'Lowest price per 100 ml', metric: 'pricePer100ml' },
  // PACKAGE
  { id: 'package_large', group: 'package', label: 'Largest package', metric: 'packageSize', direction: 'higher' },
  { id: 'package_small', group: 'package', label: 'Smallest package', metric: 'packageSize', direction: 'lower' },
];

export const SORT_GROUP_LABELS: Record<SortGroup, string> = {
  value: 'Best value',
  nutrition: 'Nutrition',
  price: 'Price',
  package: 'Package',
};

export function sortOptionById(id: string): SortOption {
  return SORT_OPTIONS.find((o) => o.id === id) ?? SORT_OPTIONS[0]!;
}

// ---------------------------------------------------------------------------
// Analysed rows
// ---------------------------------------------------------------------------

/** A product plus everything derived from it, ready for the UI. */
export interface AnalysedProduct {
  product: Product;
  metrics: ProductMetrics;
  score: ValueScore;
  /** 1-based position under the active sort. `null` when the metric is missing. */
  rank: number | null;
  /** Per-metric 1-based rank across the set, for "Ranks #1 for protein/$". */
  metricRanks: Partial<Record<MetricId, number>>;
}

/** Read a sortable number off an analysed row. */
function sortValue(row: AnalysedProduct, metric: MetricId | 'valueScore'): number | null {
  if (metric === 'valueScore') return row.score.score;
  return row.metrics.values[metric]?.value ?? null;
}

function directionOf(option: SortOption): 'higher' | 'lower' {
  if (option.direction) return option.direction;
  if (option.metric === 'valueScore') return 'higher';
  return METRIC_DIRECTION[option.metric];
}

/**
 * Rank products by one metric. Missing values get rank `null` and are pushed to
 * the end. Ties share the lower (better) rank, as in standard competition
 * ranking: 1, 2, 2, 4.
 */
export function rankByMetric(
  rows: AnalysedProduct[],
  metric: MetricId | 'valueScore',
  direction: 'higher' | 'lower',
): Map<string, number> {
  const withValues = rows
    .map((r) => ({ id: r.product.id, value: sortValue(r, metric) }))
    .filter((e): e is { id: string; value: number } => e.value !== null);

  withValues.sort((a, b) => (direction === 'higher' ? b.value - a.value : a.value - b.value));

  const ranks = new Map<string, number>();
  let lastValue: number | null = null;
  let lastRank = 0;
  withValues.forEach((entry, index) => {
    const rank = lastValue !== null && entry.value === lastValue ? lastRank : index + 1;
    ranks.set(entry.id, rank);
    lastValue = entry.value;
    lastRank = rank;
  });
  return ranks;
}

/** Metrics we always compute a per-metric rank for, to power "Ranks #1 for…". */
const RANKED_METRICS: MetricId[] = [
  'proteinPerDollar',
  'kcalPerDollar',
  'pricePer100kcal',
  'pricePer10gProtein',
  'pricePer100g',
  'pricePer100ml',
  'proteinPer100kcal',
  'protein100',
  'proteinPctCalories',
  'fibre100',
  'price',
];

/**
 * Turn raw products into fully analysed, sorted rows.
 *
 * Scores are computed across the *whole* supplied set before sorting so that a
 * product's score does not change as you re-sort.
 */
export function analyse(
  products: Product[],
  computeMetrics: (p: Product) => ProductMetrics,
  sort: SortOption,
  profile: ScoringProfile = DEFAULT_PROFILE,
): AnalysedProduct[] {
  const metrics = products.map(computeMetrics);
  const scores = scoreAll(metrics, profile);

  const rows: AnalysedProduct[] = products.map((product, i) => ({
    product,
    metrics: metrics[i]!,
    score: scores.get(product.id) ?? {
      score: null,
      components: [],
      coverage: 0,
      unavailableReason: 'Not scored.',
    },
    rank: null,
    metricRanks: {},
  }));

  for (const metric of RANKED_METRICS) {
    const ranks = rankByMetric(rows, metric, METRIC_DIRECTION[metric]);
    for (const row of rows) {
      const r = ranks.get(row.product.id);
      if (r !== undefined) row.metricRanks[metric] = r;
    }
  }

  const direction = directionOf(sort);
  const sorted = [...rows].sort((a, b) => {
    const av = sortValue(a, sort.metric);
    const bv = sortValue(b, sort.metric);
    // Missing always last, in both directions.
    if (av === null && bv === null) return a.product.name.localeCompare(b.product.name);
    if (av === null) return 1;
    if (bv === null) return -1;
    if (av === bv) return a.product.name.localeCompare(b.product.name);
    return direction === 'higher' ? bv - av : av - bv;
  });

  const sortRanks = rankByMetric(sorted, sort.metric, direction);
  for (const row of sorted) {
    row.rank = sortRanks.get(row.product.id) ?? null;
  }
  return sorted;
}

// ---------------------------------------------------------------------------
// Filtering (section 12)
// ---------------------------------------------------------------------------

export interface Filters {
  categories: string[];
  brands: string[];
  stores: string[];
  locations: string[];
  minPrice: number | null;
  maxPrice: number | null;
  minProtein: number | null;
  maxCalories: number | null;
  minPackageSize: number | null;
  maxPackageSize: number | null;
  /** Only keep products we can actually analyse nutritionally. */
  requireNutrition: boolean;
  availability: 'all' | 'online' | 'offline';
}

export function emptyFilters(): Filters {
  return {
    categories: [],
    brands: [],
    stores: [],
    locations: [],
    minPrice: null,
    maxPrice: null,
    minProtein: null,
    maxCalories: null,
    minPackageSize: null,
    maxPackageSize: null,
    requireNutrition: false,
    availability: 'all',
  };
}

export function countActiveFilters(f: Filters): number {
  let n = 0;
  if (f.categories.length) n++;
  if (f.brands.length) n++;
  if (f.stores.length) n++;
  if (f.locations.length) n++;
  if (f.minPrice !== null || f.maxPrice !== null) n++;
  if (f.minProtein !== null) n++;
  if (f.maxCalories !== null) n++;
  if (f.minPackageSize !== null || f.maxPackageSize !== null) n++;
  if (f.requireNutrition) n++;
  if (f.availability !== 'all') n++;
  return n;
}

/**
 * Apply filters.
 *
 * Numeric nutrition filters exclude products whose value is unknown: if you ask
 * for "at least 20 g protein", a product with no protein data does not qualify.
 * Text filters (brand/store) only apply when the field is known.
 */
export function applyFilters(rows: AnalysedProduct[], f: Filters): AnalysedProduct[] {
  return rows.filter(({ product, metrics }) => {
    if (f.categories.length && !(product.category && f.categories.includes(product.category))) {
      return false;
    }
    if (f.brands.length && !(product.brand && f.brands.includes(product.brand))) return false;
    if (f.stores.length && !(product.store && f.stores.includes(product.store.name))) return false;
    if (f.locations.length) {
      const loc = product.store?.location ?? product.store?.city ?? null;
      if (!loc || !f.locations.includes(loc)) return false;
    }

    const price = metrics.values.price.value;
    if (f.minPrice !== null && (price === null || price < f.minPrice)) return false;
    if (f.maxPrice !== null && (price === null || price > f.maxPrice)) return false;

    if (f.minProtein !== null) {
      const p = metrics.values.protein100.value;
      if (p === null || p < f.minProtein) return false;
    }
    if (f.maxCalories !== null) {
      const k = metrics.values.kcal100.value;
      if (k === null || k > f.maxCalories) return false;
    }

    const size = metrics.values.packageSize.value;
    if (f.minPackageSize !== null && (size === null || size < f.minPackageSize)) return false;
    if (f.maxPackageSize !== null && (size === null || size > f.maxPackageSize)) return false;

    if (f.requireNutrition && metrics.per100 === null) return false;

    if (f.availability !== 'all') {
      const online = product.store?.online;
      if (online === undefined || online === null) return false;
      if (f.availability === 'online' && !online) return false;
      if (f.availability === 'offline' && online) return false;
    }
    return true;
  });
}

/** Distinct filter facet values present in a result set, with counts. */
export interface Facets {
  categories: Array<{ value: string; count: number }>;
  brands: Array<{ value: string; count: number }>;
  stores: Array<{ value: string; count: number }>;
  locations: Array<{ value: string; count: number }>;
  priceRange: { min: number; max: number } | null;
}

export function buildFacets(rows: AnalysedProduct[]): Facets {
  const tally = (pick: (r: AnalysedProduct) => string | null | undefined) => {
    const map = new Map<string, number>();
    for (const r of rows) {
      const v = pick(r);
      if (!v) continue;
      map.set(v, (map.get(v) ?? 0) + 1);
    }
    return [...map.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
  };

  const prices = rows
    .map((r) => r.metrics.values.price.value)
    .filter((v): v is number => v !== null);

  return {
    categories: tally((r) => r.product.category),
    brands: tally((r) => r.product.brand),
    stores: tally((r) => r.product.store?.name),
    locations: tally((r) => r.product.store?.location ?? r.product.store?.city),
    priceRange: prices.length
      ? { min: Math.min(...prices), max: Math.max(...prices) }
      : null,
  };
}

// ---------------------------------------------------------------------------
// Trade-off insights (section 33)
// ---------------------------------------------------------------------------

export interface Insight {
  kind: 'tradeoff' | 'rank' | 'warning';
  text: string;
}

/**
 * Explain *why* the top result wins, in the spec's own terms:
 * "23% more expensive but provides 47% more protein".
 *
 * Only emits a claim when both sides of the comparison are actually known.
 */
export function buildInsights(rows: AnalysedProduct[], sort: SortOption): Insight[] {
  const out: Insight[] = [];
  if (rows.length < 2) return out;

  const top = rows[0]!;
  const metricLabel = sort.metric === 'valueScore' ? 'value score' : METRICS[sort.metric].label;

  // Compare the winner against the plain cheapest product.
  const cheapest = [...rows]
    .filter((r) => r.metrics.values.price.value !== null)
    .sort((a, b) => a.metrics.values.price.value! - b.metrics.values.price.value!)[0];

  if (cheapest && cheapest.product.id !== top.product.id) {
    const topPrice = top.metrics.values.price.value;
    const cheapPrice = cheapest.metrics.values.price.value;
    const topProtein = top.metrics.perPackage?.nutrients.protein ?? null;
    const cheapProtein = cheapest.metrics.perPackage?.nutrients.protein ?? null;

    if (topPrice !== null && cheapPrice !== null && topPrice > cheapPrice) {
      const priceDiff = percentDifference(topPrice, cheapPrice);
      if (topProtein !== null && cheapProtein !== null && topProtein > cheapProtein) {
        const proteinDiff = percentDifference(topProtein, cheapProtein);
        if (priceDiff !== null && proteinDiff !== null) {
          out.push({
            kind: 'tradeoff',
            text: `${top.product.name} costs ${priceDiff.toFixed(0)}% more than ${cheapest.product.name} but provides ${proteinDiff.toFixed(0)}% more protein.`,
          });
        }
      } else if (priceDiff !== null) {
        out.push({
          kind: 'tradeoff',
          text: `${top.product.name} costs ${priceDiff.toFixed(0)}% more than the cheapest option but ranks higher on ${metricLabel}.`,
        });
      }
    }
  }

  // Density vs. value can disagree — that tension is the product's whole point.
  const bestDensity = [...rows]
    .filter((r) => r.metrics.values.protein100.value !== null)
    .sort((a, b) => b.metrics.values.protein100.value! - a.metrics.values.protein100.value!)[0];
  const bestValue = [...rows]
    .filter((r) => r.metrics.values.proteinPerDollar.value !== null)
    .sort(
      (a, b) => b.metrics.values.proteinPerDollar.value! - a.metrics.values.proteinPerDollar.value!,
    )[0];

  if (bestDensity && bestValue && bestDensity.product.id !== bestValue.product.id) {
    out.push({
      kind: 'tradeoff',
      text: `${bestDensity.product.name} has the highest protein density, but ${bestValue.product.name} provides more protein per dollar.`,
    });
  }

  const missing = rows.filter((r) => r.metrics.per100 === null).length;
  if (missing > 0) {
    out.push({
      kind: 'warning',
      text: `${missing} of ${rows.length} result${rows.length === 1 ? '' : 's'} lack enough nutrition data to rank and are shown without value metrics.`,
    });
  }

  return out;
}

/** Metrics on which this product ranks #1 — the "why it's here" badge. */
export function winningMetrics(row: AnalysedProduct): MetricId[] {
  return (Object.entries(row.metricRanks) as Array<[MetricId, number]>)
    .filter(([, rank]) => rank === 1)
    .map(([metric]) => metric);
}
